import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useScopeStore } from '../../store/scopeStore'
import { globalSearch, SEARCH_MIN_LEN, type SearchHit } from '../../services/api'
import { Sym } from '../ui/Sym'

// ── Global header search (112663) ────────────────────────────────────────────
// The header input previously had no value, no onChange and no state — it
// advertised "Search tenants, users, findings…" on every page and did nothing.
//
// Navigation deliberately goes via the workspace scope rather than a per-entity
// route: /tenants/:id is internal-only, so routing there would 403 for a
// CustomerAdmin. Setting scope + landing on a scope-aware page works for every
// role and keeps the single-WorkspaceSwitcher convention intact.

/** Debounce so typing does not issue a request per keystroke. */
const DEBOUNCE_MS = 250

const GROUP_META: Record<SearchHit['kind'], { label: string; icon: string }> = {
  tenant: { label: 'Tenants', icon: 'apartment' },
  user: { label: 'Users', icon: 'person' },
  finding: { label: 'Findings', icon: 'flag' },
}

export default function GlobalSearch() {
  const navigate = useNavigate()
  const setScope = useScopeStore((s) => s.setScope)

  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [term])

  const enabled = debounced.length >= SEARCH_MIN_LEN
  const { data, isFetching } = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: ({ signal }) => globalSearch(debounced, signal),
    enabled,
    staleTime: 30_000,
  })

  // Flatten into display order so keyboard navigation has a single index space.
  const flat = useMemo<SearchHit[]>(
    () => [...(data?.tenants ?? []), ...(data?.users ?? []), ...(data?.findings ?? [])],
    [data]
  )

  // Reset the highlight whenever the result set changes, so Enter can never
  // open a stale row left selected from the previous query.
  useEffect(() => setCursor(0), [debounced, flat.length])

  // Dismiss on outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  function select(hit: SearchHit) {
    setOpen(false)
    setTerm('')
    setDebounced('')
    if (hit.kind === 'tenant') {
      setScope(hit.id)
      navigate('/dashboard')
      return
    }
    if (hit.tenantId) setScope(hit.tenantId)
    navigate(hit.kind === 'user' ? '/mfa' : '/assessments')
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
      return
    }
    if (!open || !flat.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => (c + 1) % flat.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => (c - 1 + flat.length) % flat.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const hit = flat[cursor]
      if (hit) select(hit)
    }
  }

  const showPanel = open && term.trim().length > 0
  let flatIndex = -1

  return (
    <div ref={rootRef} className="relative w-[340px] max-w-full">
      <div className="flex items-center gap-2 bg-surface border border-border rounded-[9px] px-3 py-1.5">
        <Sym name="search" size={18} className="text-text-faint flex-shrink-0" />
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => {
            setTerm(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search tenants, users, findings…"
          aria-label="Search tenants, users and findings"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="global-search-results"
          className="flex-1 min-w-0 bg-transparent text-[13px] text-text-primary placeholder:text-text-faint outline-none"
        />
        {isFetching && enabled && (
          <div className="w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
        )}
        {term && (
          <button
            onClick={() => { setTerm(''); setDebounced(''); inputRef.current?.focus() }}
            aria-label="Clear search"
            className="text-text-faint hover:text-text-primary flex-shrink-0"
          >
            <Sym name="close" size={16} />
          </button>
        )}
      </div>

      {showPanel && (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute top-full mt-1.5 left-0 right-0 z-50 bg-surface border border-border rounded-[10px] shadow-xl max-h-[420px] overflow-y-auto"
        >
          {!enabled ? (
            <div className="px-3.5 py-3 text-xs text-text-faint">
              Keep typing — at least {SEARCH_MIN_LEN} characters.
            </div>
          ) : !flat.length ? (
            <div className="px-3.5 py-3 text-[13px] text-text-muted">
              No matches for “{debounced}”
              <div className="text-xs text-text-faint mt-0.5">Tenants, users and findings you have access to.</div>
            </div>
          ) : (
            <>
              {(['tenant', 'user', 'finding'] as const).map((kind) => {
                const group = kind === 'tenant' ? data?.tenants : kind === 'user' ? data?.users : data?.findings
                if (!group?.length) return null
                return (
                  <div key={kind}>
                    <div className="px-3.5 pt-2.5 pb-1 text-[10.5px] font-semibold tracking-[0.7px] uppercase text-text-faint flex items-center gap-1.5">
                      <Sym name={GROUP_META[kind].icon} size={13} /> {GROUP_META[kind].label}
                    </div>
                    {group.map((hit) => {
                      flatIndex += 1
                      const active = flatIndex === cursor
                      const myIndex = flatIndex
                      return (
                        <button
                          key={`${hit.kind}-${hit.id}`}
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setCursor(myIndex)}
                          onClick={() => select(hit)}
                          className={`w-full text-left px-3.5 py-2 flex items-center gap-2.5 ${
                            active ? 'bg-surface-raised' : 'hover:bg-surface-raised'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] truncate flex items-center gap-1.5">
                              {hit.label}
                              {hit.isGlobalAdmin && (
                                <span className="font-mono text-[9.5px] px-1.5 py-[1px] rounded bg-warning/15 text-amber-400">GA</span>
                              )}
                            </div>
                            <div className="text-[11.5px] text-text-faint truncate">{hit.sublabel}</div>
                          </div>
                          {hit.severity && (
                            <span className="font-mono text-[9.5px] px-1.5 py-[1px] rounded bg-danger/15 text-red-400 flex-shrink-0">
                              {hit.severity}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )
              })}
              {data?.truncated && (
                // Say the list is partial rather than letting it read as complete.
                <div className="px-3.5 py-2 text-[11.5px] text-text-faint border-t border-border-soft">
                  Showing the closest matches — refine the term for more.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
